import { CenterAlignedLoader, removeAllToasts } from '@medly-components/core';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { LogoLoader } from 'components/common';
import OnboardingFormWrapper from 'components/common/OnBoardingFormWrapper';
import { OnboardingPageWrapper } from 'components/common/OnboardingPageWrapper/OnboardingPageWrapper';
import { CONTINUE, GETTING_KNOW_YOU } from 'constants/onboarding';
import { SUBMIT } from 'constants/text/common';
import { useBoolean } from 'hooks';
import { useCustomNavigate } from 'hooks/useCustomNavigate';
import { HOME_ROUTE } from '../../constants/routes';
import { QuestionnaireContext } from '../../context/QuestionnaireContext';
import { UserContext } from '../../context/UserContext';
import { useMantaCareAPI } from '../../hooks/useMantaCareAPI/useMantaCareAPI';
import { QuestionnaireForm } from './QuestionnaireForm';
import { QuestionnairePageWrapper } from './Questionnarie.Styled';
import { CreateUserQuestionnairesBody, QuestionnaireAnswer, QuestionnaireType } from './types';

export const LABEL_OPTION_SEPARATOR = '_#_';

/*
The Questionnaire component represents a dynamic questionnaire form with multiple pages.
It handles the navigation between pages, collecting user answers, and submitting the 
completed questionnaire to a server.
*/

export const Questionnaire = () => {
    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const [data, setData] = useState<QuestionnaireType>();
    const { answers } = useContext(QuestionnaireContext);
    const { setHasCompletedOnboardingQuestionnaire: setAllQuestionsFilled } = useContext(UserContext);
    const { navigateWithSearch } = useCustomNavigate();
    const [fetchingQuestionnaires, setFetchingQuestionnaires] = useState(true);
    const { loading, createUserQuestionnaires, getOnboardingQuestionnaires } = useMantaCareAPI();
    const { user } = useContext(UserContext);
    const { value: loader, setTrue: showLoader } = useBoolean(false);

    useEffect(() => {
        (async () => {
            try {
                const response = await getOnboardingQuestionnaires();
                setData(response);
            } catch (e) {
                return;
            } finally {
                setFetchingQuestionnaires(false);
            }
        })();
    }, []);

    useEffect(() => {
        return () => removeAllToasts();
    }, []);

    /**
     * Extracts the page & questions from data at a given page index.
     */
    const pageData = useMemo(() => {
        if (!data) {
            return null;
        }
        return {
            page: data.pages[currentPageIndex]
        };
    }, [currentPageIndex, data]);
    const page = pageData?.page;
    /**
     * Submits the completed questionnaire to the server.
     * Redirects the user to a home page after successful submission.
     */
    const submitQuestionnaires = useCallback(async () => {
        if (!data || !user || !user.id) {
            return;
        }
        const userQuestionnaireAnswers: QuestionnaireAnswer[] = Object.keys(answers).map(shortQuestionName => {
            const answer = answers[shortQuestionName].answer;
            const isString = typeof answer === 'string';
            const updatedAnswer = isString
                ? answer.split(LABEL_OPTION_SEPARATOR)[1]
                : answer.map((value: string) => value.split(LABEL_OPTION_SEPARATOR)[1]);
            return {
                question: answers[shortQuestionName].question,
                answer: updatedAnswer,
                shortQuestionName
            };
        });
        const createQuestionnaireBody: CreateUserQuestionnairesBody = {
            answers: userQuestionnaireAnswers,
            questionnaire_id: data.id,
            user_id: user.id
        };
        const response = await createUserQuestionnaires(user.id, createQuestionnaireBody);
        if (response?.status == 201 || response?.status == 200) {
            setAllQuestionsFilled();
            showLoader();
            setTimeout(() => {
                navigateWithSearch({ pathname: HOME_ROUTE });
            }, 5000);
        }
    }, [answers, user]);

    const isLastQuestionnairePage = data && currentPageIndex === data.pages.length - 1;

    /**
     * Handles the "Continue" button click on a questionnaire page.
     * Navigates to the next page if available, otherwise submits the questionnaire.
     */
    const onContinue = useCallback(() => {
        if (!data) {
            return;
        }
        if (!isLastQuestionnairePage) {
            setCurrentPageIndex(currentPageIndex + 1);
            window.scrollTo({
                top: 0,
                left: 0,
                behavior: 'instant'
            });
            return;
        }
        submitQuestionnaires();
    }, [isLastQuestionnairePage, data, setCurrentPageIndex, submitQuestionnaires]);

    /**
     * Handles the "Go Back" button click on a questionnaire page.
     * Navigates to the previous page if available.
     */
    const onGoBack = useCallback(() => {
        if (currentPageIndex > 0) {
            setCurrentPageIndex(currentPageIndex - 1);
        }
    }, [currentPageIndex, setCurrentPageIndex]);

    if (loader) {
        return <LogoLoader />;
    }

    if (fetchingQuestionnaires) {
        return <CenterAlignedLoader />;
    }
    return (
        <OnboardingPageWrapper
            showBackButton={currentPageIndex !== 0}
            onGoBack={onGoBack}
            showLogoutButton
            showTopLeftVector
            showBottomLeftVector
        >
            <QuestionnairePageWrapper currentPageIndex={currentPageIndex}>
                <OnboardingFormWrapper
                    header={page?.title ?? ''}
                    blueHeader={`${GETTING_KNOW_YOU}`}
                    mediumHeader={page?.description ?? ''}
                    hasDescription={Boolean(page?.description)}
                >
                    <QuestionnaireForm
                        loading={loading}
                        onContinue={onContinue}
                        page={page}
                        submitButtonLabel={isLastQuestionnairePage ? SUBMIT : CONTINUE}
                    />
                </OnboardingFormWrapper>
            </QuestionnairePageWrapper>
        </OnboardingPageWrapper>
    );
};
